import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { money } from '../../../utils/money';
import { Btn, DangerBtn, Loading, Msg, Section, Select, TextInput, fmtCell, fmtDate, panelGet, panelPost } from '../kit';
import {
  CopyId, DEFAULT_TRANSITIONS, DELIVERABLE, DELIVERY_EXTS, DELIVERY_MAX_MB, DELIVER_PATH, Fact, S, STATUS_META,
  StatusPill, daysBetween, downloadFile, dueAt, isOverdue, list, num, postMultipart, rowsOf, statusLabel, str,
} from './shared';

/**
 * One order: who bought it, what they paid, what they sent us, and the
 * workflow — start work, ask a question, cancel, deliver. Reads
 * GET ip_intel/order?order_id=…, writes POST ip_intel/order_update (status,
 * notes, assignee; every change audited server-side), POST ip_intel/resend_email,
 * and uploads the report through the raw deliver route (multipart), which also
 * e-mails the customer.
 */

const TRL_LINES: Record<number, string> = {
  1: 'Basic principles observed',
  2: 'Technology concept formulated',
  3: 'Experimental proof of concept',
  4: 'Technology validated in the lab',
  5: 'Validated in a relevant environment',
  6: 'Demonstrated in a relevant environment',
  7: 'Prototype demonstrated in an operational environment',
  8: 'System complete and qualified',
  9: 'Proven in an operational environment',
};

/** The intake's fields in reading order; anything else the form stored is
 *  listed after them under its own key, so nothing the customer sent is hidden. */
const INTAKE_FIELDS: { label: string; keys: string[] }[] = [
  { label: 'Source', keys: ['source'] },
  { label: 'Cosmos listing id', keys: ['utech_technology_id', 'technology_id', 'cosmos_id'] },
  { label: 'What they entered', keys: ['input'] },
  { label: 'Cosmos link', keys: ['cosmos_url', 'url', 'link'] },
  { label: 'Title', keys: ['title', 'technology_title', 'utech_title'] },
  { label: 'University', keys: ['university_name', 'university'] },
  { label: 'Summary', keys: ['summary', 'description', 'abstract'] },
  { label: 'Patent / application no.', keys: ['patent_number', 'application_number', 'patent_or_application', 'patent'] },
  { label: 'TRL', keys: ['trl'] },
  { label: 'Stage of development', keys: ['stage_of_development', 'stage'] },
  { label: 'Stage notes', keys: ['stage_notes', 'stage_other', 'stage_text', 'stage_detail'] },
  { label: 'Target markets', keys: ['target_markets', 'markets'] },
  { label: 'Key questions', keys: ['key_questions', 'questions'] },
  { label: 'Contact for questions', keys: ['contact'] },
  { label: 'Contact confirmed', keys: ['contact_confirm', 'contact_confirmed', 'confirm_contact'] },
  { label: 'Form revision', keys: ['revision'] },
  { label: 'Last submitted', keys: ['submitted_at'] },
];

/** ip_intel_events kinds, in words. */
const EVENT_TITLES: Record<string, string> = {
  order_created: 'Order created — Razorpay order opened', paid: 'Paid', payment_rejected: 'Payment rejected',
  duplicate_payment: 'Duplicate payment', payment_failed: 'Payment failed', refund: 'Refund at Razorpay',
  webhook: 'Razorpay webhook', email_sent: 'E-mail sent', email_failed: 'E-mail FAILED',
  intake_submitted: 'Intake submitted', intake_updated: 'Intake updated', delivered: 'Report delivered',
  delivery_downloaded: 'Customer downloaded the report', status_changed: 'Status changed',
  admin_notes_changed: 'Internal notes edited', assigned_to_changed: 'Assignee changed',
  clarification_notes_changed: 'Clarification questions edited',
};
const EMAIL_NAMES: Record<string, string> = {
  ip_intel_order_paid: 'receipt + intake link', ip_intel_intake_received: 'intake received',
  ip_intel_clarification: 'clarification questions', ip_intel_delivered: 'report ready',
  ip_intel_order_paid_known_tech: 'receipt (tech from Cosmos, intake filed automatically)',
};
const BAD_EVENTS = new Set(['payment_rejected', 'payment_failed', 'email_failed', 'duplicate_payment']);

/** One timeline row from {kind, actor, detail: {...}, at}. */
const eventLine = (e: any, currency: string): Tl => {
  const kind = str(e.kind ?? e.event ?? e.type ?? e.action);
  const d = e.detail && typeof e.detail === 'object' && !Array.isArray(e.detail) ? { ...e.detail } : {};
  let title = str(e.title ?? e.label) || EVENT_TITLES[kind] || kind || 'Event';
  if (kind === 'status_changed' && (d.from || d.to)) {
    title = `Status: ${statusLabel(d.from)} → ${statusLabel(d.to)}`;
    delete d.from; delete d.to;
  } else if ((kind === 'email_sent' || kind === 'email_failed') && d.key) {
    title = `${EVENT_TITLES[kind]} — ${EMAIL_NAMES[d.key] || d.key}`;
    delete d.key;
  } else if (kind === 'assigned_to_changed') {
    title = `Assigned to ${str(d.value) || 'nobody'}`;
    delete d.value;
  } else if (kind === 'delivered' && d.redelivery) {
    title = 'Report re-delivered';
  }
  delete d.redelivery;
  const detail = typeof e.detail === 'string' ? e.detail : Object.entries(d)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => {
      const label = k.replace(/_minor$/, '').replace(/_bytes$/, '').replace(/_/g, ' ');
      const val = /_minor$/.test(k) && Number.isFinite(Number(v)) ? money(Number(v), str(d.currency) || currency)
        : /_bytes$/.test(k) ? fmtBytes(v)
          : typeof v === 'object' ? JSON.stringify(v) : String(v);
      return `${label}: ${val}`;
    }).join(' · ');
  const by = str(e.actor ?? e.by ?? e.admin_email);
  return { at: str(e.at ?? e.created_at ?? e.when), title, detail, by: by === 'system' ? '' : by,
           tone: BAD_EVENTS.has(kind) ? 'bad' : str(e.tone) };
};

const RESEND_KINDS: Record<string, string> = {
  paid: 'Receipt + intake link', intake: 'Intake received', clarification: 'Clarification questions',
  delivered: 'Report ready (download link)',
};

const flatIntake = (intake: any): Record<string, any> => {
  if (!intake || typeof intake !== 'object') return {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(intake)) {
    // {cosmos: {...}} / {own: {...}} / {technology: {...}} fold into the top level.
    if (v && typeof v === 'object' && !Array.isArray(v) && ['cosmos', 'own', 'technology', 'own_ip'].includes(k)) {
      for (const [k2, v2] of Object.entries(v as any)) if (out[k2] === undefined) out[k2] = v2;
    } else if (out[k] === undefined) out[k] = v;
  }
  return out;
};

const isEmpty = (v: any) => v === undefined || v === null || v === ''
  || (typeof v === 'object' && !Array.isArray(v) && Object.values(v).every(x => x === null || x === undefined || x === ''));

const fmtBytes = (b: any) => {
  const n = num(b);
  if (!n) return '';
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

const fileMeta = (f: any) =>
  [fmtBytes(f.size_bytes), f.uploaded_at ? fmtDate(f.uploaded_at) : '', str(f.uploaded_by) && str(f.uploaded_by) !== 'customer' ? `by ${f.uploaded_by}` : '']
    .filter(Boolean).join(' · ');

const valueNode = (key: string, v: any): React.ReactNode => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ') || '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (key === 'source') return v === 'cosmos' ? 'Cosmos listing' : v === 'own' ? 'Own IP (uploaded)' : String(v);
  if (typeof v === 'object') {
    const parts = Object.entries(v).filter(([, x]) => x !== null && x !== undefined && x !== '')
      .map(([, x]) => (typeof x === 'object' ? JSON.stringify(x) : String(x)));
    return parts.length ? parts.join(' · ') : '—';
  }
  if (key === 'trl') {
    const n = Number(v);
    return TRL_LINES[n] ? <>{n} <span className="text-grayscale-1100 font-normal">— {TRL_LINES[n]}</span></> : String(v);
  }
  if (/url|link/i.test(key) && /^https?:\/\//i.test(String(v))) {
    return <a href={String(v)} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline break-all">{String(v)}</a>;
  }
  return <span className="whitespace-pre-wrap">{fmtCell(key, v)}</span>;
};

type Tl = { at: string; title: string; detail?: string; by?: string; tone?: string };

const OrderDrawer: React.FC<{
  orderId: string; turnaroundDays?: number; onClose: () => void; onChanged: () => void;
}> = ({ orderId, turnaroundDays = 7, onClose, onChanged }) => {
  const [raw, setRaw] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [clarify, setClarify] = useState('');
  const [notes, setNotes] = useState('');
  const [assignee, setAssignee] = useState('');
  const [admins, setAdmins] = useState<string[] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [deliverNote, setDeliverNote] = useState('');
  const [deliverArmed, setDeliverArmed] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [resendKind, setResendKind] = useState('');
  const [resendArmed, setResendArmed] = useState(false);
  // How the delivery mail carries the report (Samples tab → Paid report):
  // link | attach | both, and the size an attachment may be.
  const [deliveredMode, setDeliveredMode] = useState<{ mode: string; maxMb: number } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await panelGet(S, 'order', { order_id: orderId });
    setRaw(data); setError(error);
    const o = data?.order ?? data;
    if (o) { setNotes(str(o.admin_notes)); setAssignee(str(o.assigned_to)); }
  }, [orderId]);

  useEffect(() => { setRaw(null); setError(''); setMsg(null); load(); }, [load]);

  useEffect(() => {
    let alive = true;
    panelGet(S, 'delivery_settings').then(({ data }) => {
      const dv = data?.settings?.delivered;
      if (alive && dv) setDeliveredMode({ mode: str(dv.mode) || 'link', maxMb: num(dv.attach_max_mb) || 10 });
    });
    return () => { alive = false; };
  }, []);

  // Assignee picker offers the admin allowlist; a free-text box if it cannot be read.
  useEffect(() => {
    let alive = true;
    apiService.adminBillingAdmins(apiService.getStoredUserId() || '').then(r => {
      if (!alive) return;
      const a = r.success && r.data?.success && Array.isArray(r.data.admins)
        ? r.data.admins.filter((x: any) => x?.is_active !== false).map((x: any) => str(x.email)).filter(Boolean)
        : null;
      setAdmins(a && a.length ? a : null);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!deliverArmed) return;
    const t = setTimeout(() => setDeliverArmed(false), 6000);
    return () => clearTimeout(t);
  }, [deliverArmed]);
  useEffect(() => {
    if (!resendArmed) return;
    const t = setTimeout(() => setResendArmed(false), 6000);
    return () => clearTimeout(t);
  }, [resendArmed]);

  const o: any = raw?.order ?? raw ?? null;
  const status = str(o?.status);
  const currency = str(o?.currency) || 'INR';
  const files = rowsOf(raw?.files ?? o?.files);
  const intakeFiles = files.filter(f => str(f.kind || 'intake') === 'intake');
  const deliveryFiles = files.filter(f => str(f.kind) === 'delivery');
  const payment = raw?.payment ?? o?.payment ?? {};
  const allowed: string[] = useMemo(() => {
    const a = raw?.allowed_transitions ?? o?.allowed_transitions ?? raw?.transitions?.[status];
    const list: string[] = Array.isArray(a) ? a.map(String) : (DEFAULT_TRANSITIONS[status] || []);
    // No "Mark in progress" before the intake is in: in_progress closes the
    // intake form to the customer (the backend refuses it too).
    return o?.intake_submitted_at ? list : list.filter(s => s !== 'in_progress');
  }, [raw, o, status]);
  const canDeliver = typeof o?.deliverable === 'boolean' ? o.deliverable : DELIVERABLE.has(status);

  // ip_intel.admin_order sends the intake beside the order summary, not in it.
  const intake = useMemo(() => flatIntake(raw?.intake ?? o?.intake), [raw, o]);
  const intakeRows = useMemo(() => {
    const used = new Set<string>();
    const out: { key: string; label: string; value: any }[] = [];
    for (const f of INTAKE_FIELDS) {
      const k = f.keys.find(x => !isEmpty(intake[x]));
      f.keys.forEach(x => used.add(x));
      if (k) out.push({ key: k, label: f.label, value: intake[k] });
    }
    for (const [k, v] of Object.entries(intake)) {
      if (used.has(k) || k === 'files' || k.startsWith('_') || isEmpty(v)) continue;
      out.push({ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: v });
    }
    return out;
  }, [intake]);

  const timeline: Tl[] = useMemo(() => {
    const server = rowsOf(raw?.timeline ?? o?.timeline ?? raw?.events);
    if (server.length) {
      return server.map(e => eventLine(e, currency)).sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
    }
    if (!o) return [];
    const synth: Tl[] = [];
    if (o.created_at) synth.push({ at: o.created_at, title: 'Order created', detail: 'Razorpay order opened' });
    if (o.paid_at) synth.push({ at: o.paid_at, title: 'Paid', detail: money(num(o.total_minor), currency) });
    if (o.intake_submitted_at) synth.push({ at: o.intake_submitted_at, title: 'Intake submitted' });
    if (o.delivered_at) synth.push({ at: o.delivered_at, title: 'Delivered' });
    return synth.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  }, [raw, o, currency]);

  const update = async (body: Record<string, any>, okText: string) => {
    setBusy(Object.keys(body).join(',')); setMsg(null);
    const { data, error } = await panelPost(S, 'order_update', { order_id: orderId, ...body });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return false; }
    const mailed = data?.email_sent ?? data?.emailed;
    setMsg({ kind: 'ok', text: okText + (mailed === false ? ' (the e-mail did NOT go out — check Email › Logs)' : '') });
    await load();
    onChanged();
    return true;
  };

  const setStatus = (to: string) => update({ status: to }, `Marked ${statusLabel(to).toLowerCase()}.`);

  const sendClarification = async () => {
    const text = clarify.trim();
    if (!text) { setMsg({ kind: 'error', text: 'Write the questions for the customer first.' }); return; }
    const ok = await update({ status: 'clarification', clarification_notes: text },
      `Clarification requested — ${o?.email || 'the customer'} has been e-mailed the questions.`);
    if (ok) { setClarify(''); setClarifyOpen(false); }
  };

  const saveTeam = () => update({ admin_notes: notes, assigned_to: assignee }, 'Notes and assignee saved.');

  const pickFile = (f: File | null) => {
    setDeliverArmed(false);
    if (!f) { setFile(null); return; }
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!DELIVERY_EXTS.includes(ext)) {
      setMsg({ kind: 'error', text: `.${ext} is not accepted — use ${DELIVERY_EXTS.join(', ')}.` });
      setFile(null); if (fileRef.current) fileRef.current.value = ''; return;
    }
    if (f.size > DELIVERY_MAX_MB * 1048576) {
      setMsg({ kind: 'error', text: `That file is ${fmtBytes(f.size)}; the limit is ${DELIVERY_MAX_MB} MB.` });
      setFile(null); if (fileRef.current) fileRef.current.value = ''; return;
    }
    setMsg(null); setFile(f);
  };

  const deliver = async () => {
    if (!file) return;
    if (!deliverArmed) { setDeliverArmed(true); return; }
    setDeliverArmed(false); setBusy('deliver'); setMsg(null); setProgress(0);
    const form = new FormData();
    form.append('order_id', orderId);
    form.append('file', file, file.name);
    if (deliverNote.trim()) form.append('message', deliverNote.trim());
    const { data, error } = await postMultipart(DELIVER_PATH, form, setProgress);
    setBusy(''); setProgress(null);
    if (error) { setMsg({ kind: 'error', text: `Delivery failed: ${error}` }); return; }
    const mailed = data?.email_sent ?? data?.emailed;
    const who = o?.email || 'the customer';
    const how = data?.attached ? 'the report (attached) and the download link'
      : data?.attach_reason === 'too_large' ? 'the download link (the file was too large to attach)'
        : 'the download link';
    setMsg({ kind: 'ok', text: `Report delivered — ${mailed === false ? 'but the e-mail did NOT go out (check Email › Logs)' : `${who} has been e-mailed ${how}`}.` });
    setFile(null); setDeliverNote(''); if (fileRef.current) fileRef.current.value = '';
    await load();
    onChanged();
  };

  const resendOptions = o ? [
    ...(o.paid_at ? ['paid'] : []),
    ...(o.intake_submitted_at ? ['intake'] : []),
    ...(status === 'clarification' ? ['clarification'] : []),
    ...(status === 'delivered' ? ['delivered'] : []),
  ] : [];
  const resend = async () => {
    const kind = resendKind || resendOptions[0];
    if (!kind) return;
    if (!resendArmed) { setResendArmed(true); return; }
    setResendArmed(false); setBusy('resend'); setMsg(null);
    const { data, error } = await panelPost(S, 'resend_email', { order_id: orderId, kind });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Resend failed: ${error}` }); return; }
    setMsg(data?.sent === false
      ? { kind: 'error', text: `The “${RESEND_KINDS[kind]}” e-mail did NOT go out — check Email › Logs.` }
      : { kind: 'ok', text: `“${RESEND_KINDS[kind]}” e-mail re-sent to ${o?.email}.` });
    await load();
  };

  const dl = async (f: any) => {
    const err = await downloadFile(f);
    if (err) setMsg({ kind: 'error', text: `Download failed: ${err}` });
  };

  const due = o ? dueAt(o, turnaroundDays) : null;
  const overdue = o ? isOverdue(o, turnaroundDays) : false;
  const intakeUrl = str(o?.intake_url) || (o?.intake_token ? `/ip-intel-report/intake/${o.intake_token}` : '');
  const fullIntakeUrl = intakeUrl && !/^https?:/i.test(intakeUrl)
    ? `${window.location.origin}${intakeUrl}` : intakeUrl;
  // Where the buyer came from (utm / referrer). `source` on the summary is the
  // intake's cosmos|own, so the attribution travels as source_attribution.
  const attribution = [o?.source_attribution, o?.source].find(v => v && typeof v === 'object') || null;
  const deliveryUrl = str(o?.delivery_url);
  const payId = str(o?.razorpay_payment_id || payment?.razorpay_payment_id || payment?.id);
  const taxLabel = str(payment?.tax_label ?? o?.tax_label);
  const taxRate = payment?.tax_rate_pct ?? o?.tax_rate_pct;

  const workflow = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        {allowed.includes('in_progress') && (
          <Btn kind="primary" small onClick={() => setStatus('in_progress')} disabled={!!busy}
            title={status === 'delivered' ? 'Reopen the order' : 'The team has started on the report'}>
            {status === 'delivered' ? 'Reopen (in progress)' : 'Mark in progress'}
          </Btn>
        )}
        {allowed.includes('clarification') && (
          <Btn small onClick={() => setClarifyOpen(v => !v)} disabled={!!busy}
            title="E-mails the customer your questions and pauses the 7-day clock">
            {clarifyOpen ? 'Close clarification' : 'Request clarification…'}
          </Btn>
        )}
        {allowed.includes('cancelled') && (
          <DangerBtn small onConfirm={() => setStatus('cancelled')}>Cancel order</DangerBtn>
        )}
        {allowed.includes('refunded') && (
          <DangerBtn small onConfirm={() => setStatus('refunded')}>Mark refunded</DangerBtn>
        )}
        {!allowed.length && !canDeliver && (
          <span className="text-xs text-grayscale-1100">No further status changes from “{statusLabel(status)}”.</span>
        )}
      </div>
      {allowed.includes('refunded') && (
        <div className="text-[11px] text-grayscale-1100">
          “Mark refunded” only records it here — issue the refund itself from Razorpay first.
        </div>
      )}
      {clarifyOpen && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-col gap-2">
          <div className="text-xs text-amber-800">
            These questions are e-mailed to <b>{o?.email || 'the customer'}</b> with a link back to the intake form.
            The turnaround clock pauses until they answer.
          </div>
          <textarea value={clarify} onChange={e => setClarify(e.target.value)} rows={4}
            placeholder="e.g. Which markets matter most to you — India only, or also SE Asia?"
            className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <div className="flex gap-2">
            <Btn kind="primary" small onClick={sendClarification} disabled={!!busy || !clarify.trim()}>
              {busy ? 'Sending…' : 'Send to customer'}
            </Btn>
            <Btn small onClick={() => { setClarifyOpen(false); setClarify(''); }}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col"
        style={{ width: 'min(96vw, 60rem)' }}
        onClick={e => e.stopPropagation()} role="dialog" aria-label="IP Intel order">
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-grayscale-1300 font-grotesk font-mono">
                {o?.ref || orderId.slice(0, 8)}
              </span>
              {o && <StatusPill status={status} />}
              {overdue && <span className="text-[11px] font-bold uppercase text-red-600">overdue</span>}
            </div>
            {o && (
              <div className="text-xs text-grayscale-1100 truncate">
                {[o.name, o.email, o.organisation].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Btn small onClick={load} disabled={!!busy}>Refresh</Btn>
            <Btn small onClick={onClose}>Close</Btn>
          </div>
        </div>

        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {error && <Msg kind="error">{error}</Msg>}
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
          {!o && !error && <Loading label="Loading the order…" />}
          {o && (
            <>
              <Section title="Workflow"
                right={<span className="text-xs text-grayscale-1100">{STATUS_META[status]?.hint}</span>}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Fact label="Paid" value={o.paid_at ? fmtDate(o.paid_at) : ''} />
                  <Fact label="Intake in" value={o.intake_submitted_at ? fmtDate(o.intake_submitted_at) : ''} />
                  <Fact label={overdue ? 'Due — overdue' : 'Due'}
                    value={due ? <span className={overdue ? 'text-red-600' : ''}>{fmtDate(due.toISOString())}</span> : ''}
                    title={o.due_at ? 'From the server (clarification pauses counted)' : `Intake + ${turnaroundDays} days`} />
                  <Fact label="Delivered" value={o.delivered_at
                    ? <>{fmtDate(o.delivered_at)}{o.intake_submitted_at && <span className="text-grayscale-1100 font-normal"> · {daysBetween(o.intake_submitted_at, o.delivered_at)} d</span>}</>
                    : ''} />
                </div>
                {workflow}
                {str(o.clarification_notes) && (
                  <div className="text-xs rounded-lg border border-grayscale-300 bg-grayscale-100 px-3 py-2">
                    <div className="uppercase tracking-wide text-[10px] text-grayscale-1100 mb-0.5">Last clarification asked</div>
                    <div className="whitespace-pre-wrap text-grayscale-1300">{o.clarification_notes}</div>
                  </div>
                )}
              </Section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Customer">
                  <div className="grid grid-cols-2 gap-2">
                    <Fact label="Name" value={o.name} />
                    <Fact label="Email" value={o.email} />
                    <Fact label="Phone" value={o.phone} />
                    <Fact label="Organisation" value={o.organisation} />
                    <Fact label="Designation" value={o.designation} />
                    <Fact label="GSTIN" value={o.gstin ? <span className="font-mono">{o.gstin}</span> : ''} />
                    <Fact label="Region" value={[o.region_code, o.region_label].filter(Boolean).join(' · ')} />
                    <Fact label="Account" value={o.user_id ? <span className="font-mono text-xs">{o.user_id}</span> : 'Guest checkout'} />
                  </div>
                  {attribution && Object.keys(attribution).length > 0 && (
                    <div className="text-[11px] text-grayscale-1100 break-words">
                      <span className="uppercase tracking-wide">Came from</span>{' '}
                      {Object.entries(attribution).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}
                    </div>
                  )}
                  {fullIntakeUrl && (
                    <div className="flex items-start gap-2 text-xs">
                      <span className="text-grayscale-1100 w-24 shrink-0 pt-0.5">Intake link</span>
                      <CopyId value={fullIntakeUrl} />
                    </div>
                  )}
                  {deliveryUrl && (
                    <div className="flex items-start gap-2 text-xs">
                      <span className="text-grayscale-1100 w-24 shrink-0 pt-0.5">Report link</span>
                      <CopyId value={deliveryUrl} />
                    </div>
                  )}
                </Section>

                <Section title="Payment"
                  right={payId ? (
                    <a href={`https://dashboard.razorpay.com/app/payments/${encodeURIComponent(payId)}`}
                      target="_blank" rel="noopener noreferrer" className="text-xs text-primary-700 underline">
                      Open in Razorpay ↗
                    </a>
                  ) : undefined}>
                  <div className="grid grid-cols-3 gap-2">
                    <Fact label="Price" value={money(num(o.base_minor), currency)} />
                    <Fact label={`${taxLabel || 'Tax'}${taxRate !== undefined && taxRate !== null ? ` ${num(taxRate)}%` : ''}`}
                      value={money(num(o.tax_minor), currency)} />
                    <Fact label="Total" value={<b>{money(num(o.total_minor), currency)}</b>} />
                  </div>
                  <div className="flex flex-col gap-1 text-sm">
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="text-grayscale-1100 w-28 shrink-0 text-xs">Razorpay order</span>
                      <CopyId value={o.razorpay_order_id} />
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="text-grayscale-1100 w-28 shrink-0 text-xs">Payment</span>
                      <CopyId value={payId} />
                    </div>
                    {(payment?.method || payment?.paid_via || payment?.status) && (
                      <div className="flex gap-2 items-center flex-wrap text-xs">
                        <span className="text-grayscale-1100 w-28 shrink-0">Method · via</span>
                        <span>{[payment.method, payment.paid_via ? `confirmed by ${payment.paid_via}` : payment.status]
                          .filter(Boolean).join(' · ')}</span>
                      </div>
                    )}
                  </div>
                </Section>
              </div>

              <Section title="Intake — technology details"
                right={o.intake_submitted_at ? <span className="text-xs text-grayscale-1100">submitted {fmtDate(o.intake_submitted_at)}</span> : undefined}>
                {intakeRows.length === 0 ? (
                  <div className="text-sm text-grayscale-1100 py-2">
                    {status === 'paid' ? 'Paid — the customer has not sent the technology details yet.'
                      : status === 'created' ? 'Not paid yet.' : 'No intake on file.'}
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {intakeRows.map(r => (
                      <div key={r.key} className="flex gap-3 items-start text-sm py-1.5 border-b border-grayscale-200 last:border-0">
                        <span className="text-grayscale-1100 w-40 shrink-0 text-xs pt-0.5">{r.label}</span>
                        <span className="text-grayscale-1300 min-w-0 break-words">{valueNode(r.key, r.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {intakeFiles.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">Files from the customer</div>
                    {intakeFiles.map((f, i) => (
                      <div key={f.id ?? i} className={`flex items-center justify-between gap-2 text-sm border border-grayscale-300 rounded-lg px-3 py-1.5 ${f.removed_at ? 'opacity-60' : ''}`}>
                        <span className="min-w-0 truncate" title={f.filename}>
                          {f.filename || 'file'}
                          <span className="text-xs text-grayscale-1100"> · {fileMeta(f)}</span>
                          {f.removed_at && <span className="ml-1 text-[10px] font-bold uppercase text-grayscale-1100" title={`Replaced ${fmtDate(f.removed_at)}`}>replaced</span>}
                        </span>
                        <Btn small onClick={() => dl(f)}>Download</Btn>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Section title="Deliver the report"
                right={deliveryFiles.length ? <span className="text-xs text-grayscale-1100">{deliveryFiles.length} version{deliveryFiles.length === 1 ? '' : 's'} sent</span> : undefined}>
                {deliveryFiles.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {deliveryFiles.map((f, i) => (
                      <div key={f.id ?? i} className={`flex items-center justify-between gap-2 text-sm border rounded-lg px-3 py-1.5 ${f.removed_at ? 'border-grayscale-300 opacity-60' : 'border-green-200 bg-green-50'}`}>
                        <span className="min-w-0 truncate" title={f.filename}>
                          {f.filename || 'report'}
                          <span className="text-xs text-grayscale-1100"> · {fileMeta(f)}</span>
                          {f.removed_at && <span className="ml-1 text-[10px] font-bold uppercase text-grayscale-1100" title={`Replaced ${fmtDate(f.removed_at)}`}>replaced</span>}
                        </span>
                        <Btn small onClick={() => dl(f)}>Download</Btn>
                      </div>
                    ))}
                  </div>
                )}
                {canDeliver ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <input ref={fileRef} type="file" accept={DELIVERY_EXTS.map(e => `.${e}`).join(',')}
                        onChange={e => pickFile(e.target.files?.[0] || null)}
                        className="text-sm file:mr-3 file:py-1.5 file:px-3.5 file:rounded-full file:border file:border-grayscale-400 file:bg-white file:text-grayscale-1200 file:text-sm" />
                      {file && <span className="text-xs text-grayscale-1100">{fmtBytes(file.size)}</span>}
                    </div>
                    <textarea value={deliverNote} onChange={e => setDeliverNote(e.target.value)} rows={2}
                      placeholder="Optional note for the customer, included in the e-mail"
                      className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
                    {progress !== null && (
                      <div className="flex items-center gap-2 text-xs text-grayscale-1100">
                        <div className="flex-1 bg-grayscale-200 rounded-full h-2 overflow-hidden">
                          <div className="h-2 bg-primary-500 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                        {progress < 100 ? `${progress}%` : 'Processing…'}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Btn kind="primary" small onClick={deliver} disabled={!file || !!busy}>
                        {busy === 'deliver' ? 'Uploading…'
                          : deliverArmed ? `Confirm — e-mail ${o.email || 'the customer'}`
                            : status === 'delivered' ? 'Upload & re-deliver' : 'Upload & deliver'}
                      </Btn>
                      <span className="text-[11px] text-grayscale-1100">
                        Stores the file, marks the order delivered and e-mails the customer a download link
                        {deliveredMode && deliveredMode.mode !== 'link' ? ` (the file is attached too when it is up to ${deliveredMode.maxMb} MB)` : ''}.
                        {` ${DELIVERY_EXTS.join(', ')} · up to ${DELIVERY_MAX_MB} MB.`}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-grayscale-1100">
                    {status === 'paid' || status === 'created'
                      ? 'Delivery opens once the customer has sent the intake.'
                      : `Nothing to deliver for a ${statusLabel(status).toLowerCase()} order.`}
                  </div>
                )}
              </Section>

              <Section title="Team">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-grayscale-1100">Assignee</span>
                  {admins ? (
                    <Select value={assignee} onChange={setAssignee}
                      options={['', ...admins, ...(assignee && !admins.includes(assignee) ? [assignee] : [])]}
                      labels={{ '': 'Unassigned' }} />
                  ) : (
                    <TextInput value={assignee} onChange={setAssignee} placeholder="name@universitytech.io" />
                  )}
                </div>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                  placeholder="Internal notes — never shown to the customer"
                  className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
                <div className="flex gap-2 items-center">
                  <Btn kind="primary" small onClick={saveTeam}
                    disabled={!!busy || (notes === str(o.admin_notes) && assignee === str(o.assigned_to))}>
                    Save notes & assignee
                  </Btn>
                  {(notes !== str(o.admin_notes) || assignee !== str(o.assigned_to)) && (
                    <span className="text-[11px] text-amber-700">unsaved changes</span>
                  )}
                </div>
              </Section>

              {resendOptions.length > 0 && (
                <Section title="Re-send an e-mail">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={resendKind || resendOptions[0]} onChange={v => { setResendKind(v); setResendArmed(false); }}
                      options={resendOptions} labels={RESEND_KINDS} />
                    <Btn small kind={resendArmed ? 'primary' : 'subtle'} onClick={resend} disabled={!!busy}>
                      {busy === 'resend' ? 'Sending…' : resendArmed ? `Confirm — e-mail ${o.email}` : 'Re-send'}
                    </Btn>
                    <span className="text-[11px] text-grayscale-1100">
                      For a customer who lost the original. Sent from inbox@universitytech.io; it goes on the timeline.
                    </span>
                  </div>
                </Section>
              )}

              <Section title="Timeline">
                {timeline.length === 0 ? (
                  <div className="text-sm text-grayscale-1100 py-2 text-center">No events recorded.</div>
                ) : (
                  <ol className="flex flex-col">
                    {timeline.map((e, i) => (
                      <li key={i} className="flex gap-3 text-sm py-1.5 border-b border-grayscale-200 last:border-0">
                        <span className="w-32 shrink-0 text-[11px] text-grayscale-1100 pt-0.5" title={e.at}>{fmtDate(e.at)}</span>
                        <span className="min-w-0">
                          <span className={`font-medium ${e.tone === 'bad' ? 'text-red-600' : 'text-grayscale-1300'}`}>{e.title}</span>
                          {e.by && <span className="text-[11px] text-grayscale-1100"> · {e.by}</span>}
                          {e.detail && <span className="block text-xs text-grayscale-1100 whitespace-pre-wrap break-words">{e.detail}</span>}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </Section>

              {list(o.warnings).length > 0 && (
                <Msg kind="error">{list(o.warnings).join(' · ')}</Msg>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDrawer;
